<script setup lang="ts">
import { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models/statsigEvents/statsigEventsMetadata';
import { usePaxStore } from '@apps/main/new-desktop/core/stores/pax/paxStore';
import {
  HotelFilter,
  HotelFiltersModal,
} from '@apps/main/new-desktop/modules/hotelFilters';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import {
  useEditingIngredientStore,
  useRecipeBackupStore,
} from '@apps/main/new-desktop/core/stores';
import { PeekPriceModal } from '@apps/main/new-desktop/modules/peekPriceModal';

import { DestinationSelectorModal } from '../../../modules/destinationSelector';
import { PaxSelectorModal } from '../../../modules/paxSelector';
import { WhyWeSuggestModal } from '../../../modules/suggestionTypeSelector';
import { SemboBenefitsModal } from '../../../modules/semboBenefits';
import { HotelSelectionModalType } from '../models/hotelSelectionModals';
import {
  useHotelSelectionMapStore,
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';
import { useHotelSelectionDestinationSelectorStore } from '../stores/destinationSelector/destinationSelectorStore';

const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const destinationSelectorStore = useHotelSelectionDestinationSelectorStore();
const paxStore = usePaxStore();
const editingIngredientStore = useEditingIngredientStore();
const filtersStore = useFiltersStore();
const recipeBackupStore = useRecipeBackupStore();

function confirmPeekPriceModal(): void {
  recipeBackupStore.clearBackup();
  hotelSelectionModalsStore.confirmPeekPriceModal();
}

function onUpdateFilter(update: HotelFilterUpdate): void {
  if (update.type === HotelFilter.NeighborhoodPolygons) {
    hotelSelectionMapStore.zoomOnMarkerBounds();
  }

  filtersStore.updateHotelFilter(
    update,
    RemoveHotelSelectionFiltersOrigin.FilterList
  );
}
</script>

<template>
  <HotelFiltersModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.MobileFiltersModal
    "
    :ingredient="editingIngredientStore.editingStayIngredient"
    @close="hotelSelectionModalsStore.closeHotelSelectionModal($event)"
    @update-filter="onUpdateFilter"
    @apply-filter="
      hotelSelectionModalsStore.closeHotelSelectionModal(
        CloseHotelSelectionFiltersOrigin.ShowResultsButton
      )
    "
    @reset-filters="hotelSelectionStore.resetAllFilters"
  />

  <PaxSelectorModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.PaxSelector
    "
    :initial-paxes="paxStore.initialPaxes"
    :should-show-tickets-warning="paxStore.shouldShowTicketsWarning"
    :should-show-room-selection-warning="
      paxStore.shouldShowRoomSelectionWarning
    "
    :should-show-number-of-vehicles-updated-warning="false"
    :should-show-number-of-vehicles-will-be-updated-warning="
      paxStore.shouldShowNumberOfVehiclesWillBeUpdatedWarning
    "
    @confirm-modal="hotelSelectionModalsStore.confirmPaxSelectorModal"
    @cancel-modal="hotelSelectionModalsStore.closeHotelSelectionModal"
  />

  <WhyWeSuggestModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.WhyWeSuggest
    "
    @close="hotelSelectionModalsStore.closeHotelSelectionModal"
  />

  <DestinationSelectorModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.DestinationSelector
    "
    :search-text="destinationSelectorStore.searchText"
    @close-modal="hotelSelectionModalsStore.closeHotelSelectionModal"
    @change-destination="
      hotelSelectionModalsStore.confirmDestinationSelectorModal
    "
  />
  <SemboBenefitsModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.SemboBenefits
    "
    @close-modal="hotelSelectionModalsStore.closeHotelSelectionModal"
  />

  <PeekPriceModal
    v-if="
      hotelSelectionModalsStore.hotelSelectionModalType ===
      HotelSelectionModalType.PeekPrice
    "
    @confirm="confirmPeekPriceModal()"
    @cancel="hotelSelectionModalsStore.closePeekPriceModal()"
  />
</template>
